import { isVideoStation } from '@app/core/stations/helpers';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useStreamSettingsStore } from '@app/modules/logger/screens/stream/stores/stream-screen-settings.store';
import { useStreamSelection } from '@app/modules/logger/screens/stream/stores/useStreamSelection';
import { WebAppMediaExportJobEnqueueDtoTypeEnum } from '@mi-front/api-v2';
import { toDateTimeStr } from '@mi-front/core';
import { useCallback } from 'react';
import { useDownloadMedia } from './useDownloadMedia';

export interface UseDownloadMediaFromSelectionReturn {
  /** Starts download for the current selection; no-op if there is no selection. */
  downloadMedia: () => void;
  /** Starts an audio download for the current selection; no-op if there is no selection. */
  downloadAudioMedia: () => void;
  isPreparingVideoDownload: boolean;
  isPreparingAudioDownload: boolean;
}

/**
 * Adapter that wires the DownloadMedia feature to stream selection.
 * Used by the selection menu and hotkeys: resolves station and selection range, then calls the core startDownload.
 * Syncs audio/video preparing state to the stream settings store so menu loading stays in sync when triggered via hotkeys.
 */
export function useDownloadMediaFromSelection(): UseDownloadMediaFromSelectionReturn {
  const station = useActiveStation();
  const { selectionRange, selectionExists } = useStreamSelection();
  const isPreparingVideoDownload = useStreamSettingsStore(
    (s) => s.isPreparingSelectionVideoDownload,
  );
  const isPreparingAudioDownload = useStreamSettingsStore(
    (s) => s.isPreparingSelectionAudioDownload,
  );
  const setPreparingSelectionVideoDownload = useStreamSettingsStore(
    (s) => s.setPreparingSelectionVideoDownload,
  );
  const setPreparingSelectionAudioDownload = useStreamSettingsStore(
    (s) => s.setPreparingSelectionAudioDownload,
  );
  const { startDownload: startVideoDownload } = useDownloadMedia({
    onPreparingChange: setPreparingSelectionVideoDownload,
  });
  const { startDownload: startAudioDownload } = useDownloadMedia({
    onPreparingChange: setPreparingSelectionAudioDownload,
  });

  const downloadMedia = useCallback(() => {
    if (!selectionExists) {
      return;
    }

    const { from, to } = selectionRange;
    const type = isVideoStation(station)
      ? WebAppMediaExportJobEnqueueDtoTypeEnum.VIDEO
      : WebAppMediaExportJobEnqueueDtoTypeEnum.AUDIO;

    const params = {
      stationId: station.id,
      type,
      from: toDateTimeStr(from),
      to: toDateTimeStr(to),
    };

    if (type === WebAppMediaExportJobEnqueueDtoTypeEnum.VIDEO) {
      startVideoDownload(params);
      return;
    }

    startAudioDownload(params);
  }, [selectionExists, selectionRange, startAudioDownload, startVideoDownload, station]);

  const downloadAudioMedia = useCallback(() => {
    if (!selectionExists) {
      return;
    }

    const { from, to } = selectionRange;

    startAudioDownload({
      stationId: station.id,
      type: WebAppMediaExportJobEnqueueDtoTypeEnum.AUDIO,
      from: toDateTimeStr(from),
      to: toDateTimeStr(to),
    });
  }, [selectionExists, selectionRange, station, startAudioDownload]);

  return { downloadMedia, downloadAudioMedia, isPreparingVideoDownload, isPreparingAudioDownload };
}
